import type { AccountCode, JobTypeCode, TradeCode } from "@utils/BasicTypes";
import { api, state, temp, ui } from "../../interfaces/interfaces";

import type { APIKeys } from "@reducers/actions/apiActions/interface";
import { AddressUtils } from "@utils/address";
import type { ClientScope } from "@utils/address/model";
import type { RightioWBSAPIModelsSPSpRFTrackFastJobsSetupBookingResult } from "@utils/apis/wbs/model";
import StorageUtils from "@services/storage";
import type { TemplateTEMPObject } from "@services/storage/storeTemplate";
import { apiChargesMiddleware } from "./charges";
import { dispatchSetMultipleState } from "@reducers/stateReducer";
import { dispatchSetMultipleUI } from "@reducers/uiReducer";
import { mapSicCodeToDescription } from "@utils/sicCodesMapper";
import subscriptions from "@services/subscriptions/instance";

export const APIprocessor = async (
  action: {
    payload: {
      name: APIKeys;
      value: any;
      body: {
        settingName: keyof TemplateTEMPObject;
        tradeCode: TradeCode;
        accountCode: AccountCode;
        jobTypeCode: JobTypeCode;
        postcode: string;
        callTypeId: number;
      };
    };
  },
  next: any,
) => {
  switch (action.payload.name) {
    case "SetBookingId":
      action.payload.value = action.payload.value[0].id;
      ui.BookingId = action.payload.value;
      return next(action);

    case "SetupBooking": {
      const setupBooking = action.payload
        .value[0] as RightioWBSAPIModelsSPSpRFTrackFastJobsSetupBookingResult;
      state.CallTypeGroup = setupBooking.callTypeGroup ?? undefined;
      return next(action);
    }

    case "Last2ByMob":
      action.payload.name = "Last2";
      action.payload.value = action.payload.value.sort(
        (o1: { dateTimeReceived: Date }, o2: { dateTimeReceived: Date }) => {
          return o1.dateTimeReceived ? -1 : o2.dateTimeReceived ? 1 : 0;
        },
      );
      temp.Last2 = action.payload.value;
      return next(action);

    case "CompletedJobs":
      if (action.payload.value)
        temp[action.payload.name] = {
          [action.payload.value.jobId]: action.payload.value,
        };
      return next(action);

    case "JobReviews":
      if (action.payload.value)
        temp[action.payload.name] = {
          [action.payload.value.jobId]: action.payload.value,
        };
      state.JobReviewsFetched = true;
      return next(action);

    case "Last2ByLan":
      action.payload.name = "Last2";
      action.payload.value = action.payload.value.sort(
        (o1: { dateTimeReceived: any }, o2: { dateTimeReceived: any }) => {
          return o1.dateTimeReceived ? -1 : o2.dateTimeReceived ? 1 : 0;
        },
      );
      temp.Last2 = action.payload.value;
      return next(action);

    case "Last2":
      temp.Last2 = action.payload.value;

      return next(action);

    case "GetSetting":
      temp[action.payload.body.settingName] = action.payload.value[0]
        .settingValue as never;
      return next(action);

    case "AddressSearch":
      {
        // check if there are any address in the payload
        const isFull = action.payload.value?.length > 0; // if there are any addresses in the payload
        //CarePlanCustomerAddress CarePlanPlanDetailsAddress CarePlanBillingAddress BookingPaymentAddress PropertyAddress

        // get scope
        const scope = (action.payload.body as any).scope as
          | ClientScope
          | "Reload";
        const tempScope =
          scope === "Reload" || scope === ("Property" as ClientScope)
            ? "PropertyAddress"
            : scope;
        const indexedDBScope = AddressUtils.convertToIndexedDBScope(
          scope as ClientScope,
        );

        if (isFull) {
          // generate results
          const addressResources = AddressUtils.processAddressesFromDB({
            addresses: action.payload.value,
            scope: indexedDBScope,
          });

          // update cache
          await StorageUtils.replaceValuesInStoreInIndexedDB({
            tableName:
              `${indexedDBScope.replace("Reload", "Property")}Addresses` as any,
            values: addressResources,
          });
          temp.ProblemAddressSearchDone = true; //temp.ProblemAddressSearchDone = true;}

          // if addresses are for property
          if (scope === "PropertyAddress") {
            // mark postcode as valid
            temp.ProblemAddressPostcodeValid = true;
            if (
              // if we have no address selected already display modal
              ui.PropertyAddressPostcode !== ui.ProblemInputPostcode ||
              ui.PropertyAddressStreet === null ||
              ui.PropertyAddressPostcode !== ui.ProblemInputPostcode ||
              ui.ProblemInputPostcode === ""
            ) {
              if (ui.PropertyAddressPostcode !== ui.ProblemInputPostcode) {
                // reset property fields as well in job object
                dispatchSetMultipleUI({
                  PropertyAddressProperty: "",
                  PropertyAddressBusinessName: "",
                  PropertyAddressStreet: "",
                  PropertyAddressLocality: "",
                  PropertyAddressCity: "",
                  PropertyAddressSelectedAddress: undefined,
                });
                dispatchSetMultipleState({
                  JobProperty: "",
                  JobStreet: "",
                  JobCounty: "",
                  JobLocality: "",
                  JobTown: "",
                });
              }
              ui.PropertyAddressPostcode = ui.ProblemInputPostcode; // set postcode
            }
            // const token = AddressUtils.cancellationTokens.generateToken();
            // await AddressUtils.checkCoverage({
            //   ui,
            //   token,
            //   api,
            //   callbackFn: (value: boolean) => {
            //     temp.ProblemCoverage = value;
            //   },
            // });
          }

          // prepare message about results
          ui.ProblemMsg = `Post code Valid! Found ${action.payload.value.length} addresses in the area.`;
          ui.ProblemMsgVariant = "success";

          // update length of addresses
          temp[
            `${scope === "Reload" ? "PropertyAddress" : (tempScope as "CarePlanCustomerAddress" | "CarePlanPlanDetailsAddress" | "CarePlanBillingAddress" | "BookingPaymentAddress" | "PropertyAddress")}AddressesLength`
          ] = action.payload.value.length;

          // auto show modal
          if (scope === "PropertyAddress") {
            ui.PropertyAddressModalOpen = true;
          }
          switch (scope) {
            case "PropertyAddress":
              ui.PropertyAddressModalOpen = true;
              break;
            case "CarePlanCustomerAddress":
              ui.CarePlanCustomerAddressModalOpen = true;
              break;
            case "CarePlanPlanDetailsAddress":
              ui.CarePlanPlanDetailsAddressModalOpen = true;
              break;
            case "CarePlanBillingAddress":
              ui.CarePlanBillingAddressModalOpen = true;
              break;
            case "BookingPaymentAddress":
              ui.BookingPaymentAddressModalOpen = true;
              break;
          }
          // StorageUtils.replaceBulkValues("PropertyAddresses", addressResources);

          action.payload.value = addressResources;
        } else {
          // otherwise if no addresses are found
          // prepare message about results
          if (action.payload?.value?.error)
            ui.ProblemMsg = action.payload.value.message;
          else
            ui.ProblemMsg = `Post code Invalid! Possibly new area!? Re-check details!`;
          ui.ProblemMsgVariant = "error";

          // mark postcode as invalid
          if (scope === "PropertyAddress")
            temp.ProblemAddressPostcodeValid = false;
        }

        // show message
        ui.ProblemShowMsg = true; // ui.ProblemShowMsg = true;

        state.ForceHouseIconCount = true;
      }
      return next(action);

    case "PaymentMethods":
      state.PaymentMethods = action.payload.value.map((pm: any) => ({
        name: pm.paymentMethodDescription,
        id: pm.paymentMethodId,
      }));
      return next(action);

    case "LandlordAddresses":
    case "ServiceAgreementCustomerAddresses":
    case "ServiceAgreementPolicyAddresses":
    case "ServiceAgreementBillingAddresses":
    case "BookingPaymentAddresses": {
      await StorageUtils.deleteValuesFromStoreInIndexedDB({
        tableName: action.payload.name,
      });

      const AddressesLength = action.payload.value.length;
      if (AddressesLength > 0) {
        ui.ProblemMsg = `Post code Valid! Found ${AddressesLength} addresses in the area.`;
        ui.ProblemMsgVariant = "success";
      } else {
        if (action.payload?.value?.error)
          ui.ProblemMsg = action.payload.value.message;
        else
          ui.ProblemMsg = `Post code Invalid! Possibly new area!? Re-check details!`;
        ui.ProblemMsgVariant = "error";
      }
      ui.ProblemShowMsg = true;

      let tempKey: string = action.payload.name;
      if (action.payload.name === "ServiceAgreementCustomerAddresses") {
        tempKey = "CarePlanCustomerAddressAddresses";
      }
      if (action.payload.name === "ServiceAgreementPolicyAddresses") {
        tempKey = "CarePlanPlanDetailsAddressAddresses";
      }
      if (action.payload.name === "ServiceAgreementBillingAddresses") {
        tempKey = "CarePlanBillingAddressAddresses";
      }

      temp[tempKey as keyof TemplateTEMPObject] = action.payload.value as never;
      await StorageUtils.setValuesInStoreInIndexedDB({
        tableName: action.payload.name,
        values: action.payload.value,
      });

      return next(action);
    }

    case "ServiceAgreementCustomer":
      return next(action);

    case "CallsHistory":
      temp[action.payload.name] = action.payload.value;
      return next(action);

    case "DestinationNumber":
      // get account code related to real incoming number
      await api.fetchAccountCode({
        accountCode: action.payload.value.accountCode,
      });
      ui.CallFrom = action.payload.value.source;
      ui.CallTo = action.payload.value.destination;
      temp[action.payload.name] = action.payload.value;
      return next(action);

    case "AccountCode":
      ui.CallCampaign = `${
        action.payload.value.bookingFormDescription ||
        action.payload.value.accountDescription ||
        ""
      }`;
      ui.CallTo = action.payload.value.localNumber;
      subscriptions.publish("AccountCode", action.payload.value.accountCode);
      state.Account = action.payload.value;
      return next(action);

    case "Charge":
      apiChargesMiddleware(action, next);
      break;

    case "CompanyProfile":
      if (action.payload.value) {
        if (action.payload.value?.sic_codes?.length > 0) {
          action.payload.value.sic_codes = action.payload.value.sic_codes.map(
            (sic: string) => {
              return {
                sic_code: sic,
                description: mapSicCodeToDescription(sic),
              };
            },
          );
        }
      }

      return next(action);

    case "AvailableOffers":
      temp.AvailableOffers = action.payload.value;
      return next(action);

    case "AvailableOffersV2":
      temp.AvailableOffers = action.payload.value;
      return next(action);

    case "ServiceAgreementsAvailability":
      state[action.payload.name] = action.payload.value;
      return next(action);

    case "GetCallMeBackQueue":
      ui.CallMeBacksQueue = action.payload?.value?.filter(
        (x: any) => !x.removed,
      );
      return next(action);

    default:
      // cache[action.payload.name as keyof TemplateCACHEObject] = action.payload as never;
      return next(action);
  }
};
